import { useEffect, useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { LockKeyhole, WifiOff } from "lucide-react";
import { Toaster, toast } from "sonner";
import { db } from "./db/database";
import { ensurePlatformSession, signIn, signOut } from "./services/auth";
import { getInventoryRows, saveSettings } from "./services/inventory";
import { installAutoSync, requestAutoSync, syncNow } from "./services/sync";
import { registerInventoryTools } from "./services/webmcp";
import { isAndroidApp } from "./services/platform";
import type { AuthSession, InventoryRow } from "./types";
import { Layout, type PageKey } from "./components/Layout";
import { ProductForm } from "./components/ProductForm";
import { QuickMovement } from "./components/QuickMovement";
import { InstallPrompt } from "./components/InstallPrompt";
import { Dashboard } from "./pages/Dashboard";
import { InventoryPage } from "./pages/InventoryPage";
import { ProductsPage } from "./pages/ProductsPage";
import { MovementsPage } from "./pages/MovementsPage";
import { ImportsPage } from "./pages/ImportsPage";
import { AlertsPage, HistoryPage, SettingsPage, SyncPage, UsersPage } from "./pages/SupportPages";

export default function App() {
  const nativeAndroid = isAndroidApp();
  const [session, setSession] = useState<AuthSession>();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [page, setPage] = useState<PageKey>("dashboard");
  const [productOpen, setProductOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<string>();
  const [syncLabel, setSyncLabel] = useState("Online");
  const [onlineRevision, setOnlineRevision] = useState(0);

  const rows = useLiveQuery(() => getInventoryRows(), [], []) ?? [];
  const movements = useLiveQuery(() => db.movements.orderBy("createdAt").reverse().toArray(), [], []) ?? [];
  const imports = useLiveQuery(() => db.imports.orderBy("createdAt").reverse().toArray(), [], []) ?? [];
  const pending = useLiveQuery(() => db.pendingSync.orderBy("createdAt").reverse().toArray(), [], []) ?? [];
  const logs = useLiveQuery(() => db.auditLogs.orderBy("createdAt").reverse().toArray(), [], []) ?? [];
  const users = useLiveQuery(() => db.users.toArray(), [], []) ?? [];
  const aliases = useLiveQuery(() => db.variantAliases.toArray(), [], []) ?? [];
  const marketplaceListings = useLiveQuery(() => db.marketplaceListings.toArray(), [], []) ?? [];
  const settings = useLiveQuery(() => db.settings.get("app"), [], undefined);
  const pendingCount = pending.filter((item) => item.status !== "sincronizado").length;

  useEffect(() => { ensurePlatformSession().then((saved) => { setSession(saved); setSessionChecked(true); }); }, []);
  useEffect(() => {
    const handler = () => setOnlineRevision((value) => value + 1);
    window.addEventListener("online", handler); window.addEventListener("offline", handler);
    return () => { window.removeEventListener("online", handler); window.removeEventListener("offline", handler); };
  }, []);
  useEffect(() => {
    if (!settings) return;
    const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const dark = settings.theme === "escuro" || (settings.theme === "sistema" && systemDark);
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#191716" : "#b8897c");
  }, [settings]);
  useEffect(() => {
    if (!session) return;
    return installAutoSync((status) => setSyncLabel(status === "sincronizando" ? "Sincronizando…" : status === "erro" ? "Falha na sincronização" : "Sincronizado"));
  }, [session]);
  useEffect(() => {
    if (!session || !pending.some((item) => item.status === "pendente")) return;
    requestAutoSync();
  }, [session, pending]);
  useEffect(() => session ? registerInventoryTools() : undefined, [session]);

  const handleSync = async () => {
    setSyncLabel("Sincronizando…");
    try {
      const result = await syncNow();
      setSyncLabel(settings?.apiUrl ? "Sincronizado" : "Modo local");
      if (!settings?.apiUrl) toast.info("Configure a URL da API em Configurações. Seus dados continuam salvos neste aparelho.");
      else toast.success(result.conflicts ? `${result.conflicts} conflito(s) precisam de revisão.` : "Sincronização concluída.");
    } catch (error) { setSyncLabel("Falha na sincronização"); toast.error(error instanceof Error ? error.message : "Não foi possível sincronizar."); }
  };
  const openMovement = (row?: InventoryRow) => { setSelectedVariant(row?.variant.id); setMovementOpen(true); };
  const cycleTheme = async () => {
    if (!settings) return;
    const dark = document.documentElement.dataset.theme === "dark";
    await saveSettings({ theme: dark ? "claro" : "escuro" });
  };
  const handleSignOut = async () => {
    if (nativeAndroid) return;
    await signOut();
    setSession(undefined);
  };

  if (!sessionChecked) return <div className="boot-screen"><img src="/logo-wg.jpeg" alt="WG Tricot" /><span>Preparando seu estoque…</span></div>;
  if (!session) return <Login onSignedIn={setSession} apiConfigured={Boolean(settings?.apiUrl)} online={navigator.onLine} revision={onlineRevision} />;
  if (!settings) return null;

  const content = (() => {
    switch (page) {
      case "dashboard": return <Dashboard rows={rows} movements={movements} imports={imports} onMove={() => openMovement()} />;
      case "estoque": return <InventoryPage rows={rows} onMove={openMovement} onHistory={(row) => { setSelectedVariant(row.variant.id); setPage("movimentacoes"); }} />;
      case "produtos": return <ProductsPage rows={rows} aliases={aliases} listings={marketplaceListings} onNew={() => setProductOpen(true)} onMove={openMovement} onHistory={(row) => { setSelectedVariant(row.variant.id); setPage("movimentacoes"); }} />;
      case "movimentacoes": return <MovementsPage movements={selectedVariant ? movements.filter((item) => item.variantId === selectedVariant) : movements} rows={rows} />;
      case "importacoes": return <ImportsPage imports={imports} />;
      case "alertas": return <AlertsPage rows={rows} />;
      case "historico": return <HistoryPage logs={logs} />;
      case "sincronizacao": return <SyncPage pending={pending} lastSync={settings.lastSync} onSync={handleSync} />;
      case "usuarios": return <UsersPage users={users} />;
      case "configuracoes": return <SettingsPage settings={settings} />;
    }
  })();

  return <>
    <Layout page={page} onPage={(next) => { setSelectedVariant(undefined); setPage(next); }} settings={settings} user={session.user} nativeAndroid={nativeAndroid} pendingCount={pendingCount} syncLabel={navigator.onLine ? syncLabel : "Trabalhando offline"} onSync={handleSync} onQuickMovement={() => openMovement()} onTheme={cycleTheme} onSignOut={handleSignOut}>
      {content}
    </Layout>
    <ProductForm open={productOpen} onClose={() => setProductOpen(false)} />
    <QuickMovement open={movementOpen} onClose={() => { setMovementOpen(false); setSelectedVariant(undefined); }} rows={rows} initialVariantId={selectedVariant} />
    <InstallPrompt />
    <Toaster richColors position="top-right" />
  </>;
}

function Login({ onSignedIn, apiConfigured, online }: { onSignedIn: (session: AuthSession) => void; apiConfigured: boolean; online: boolean; revision: number }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setLoading(true);
    try { onSignedIn(await signIn(email, password)); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível entrar."); }
    finally { setLoading(false); }
  };
  return <main className="login-page"><section className="login-brand"><img src="/logo-wg.jpeg" alt="WG Tricot" /><div><span className="eyebrow">Estoque inteligente</span><h1>Controle que acompanha o ritmo da sua produção.</h1><p>Galpão, malharia e vendas em uma única visão — disponível até quando a internet parar.</p></div><div className="login-features"><span><i>01</i>Histórico completo de cada alteração</span><span><i>02</i>Fila offline sem perda de movimentos</span><span><i>03</i>Importação segura de vendas</span></div></section><section className="login-form-wrap"><form className="login-card" onSubmit={submit}><div className="mobile-login-logo"><img src="/logo-wg.jpeg" alt="WG Tricot" /></div><span className="lock-icon"><LockKeyhole size={22} /></span><h2>Entrar no estoque</h2><p>{apiConfigured ? "Use seu acesso cadastrado no sistema." : "Não foi possível localizar a API do servidor."}</p>{!online && <div className="offline-login-note"><WifiOff size={17} />Sem internet. Uma sessão já autenticada continua funcionando offline.</div>}<label><span>E-mail</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" /></label><label><span>Senha</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></label><button className="primary-button login-button" disabled={loading || !apiConfigured}>{loading ? "Entrando…" : "Entrar"}</button>{!apiConfigured && <small className="server-note">Confirme se a pasta <strong>api</strong> foi enviada junto com os arquivos do site.</small>}</form></section><Toaster richColors position="top-right" /></main>;
}
